import { useState, useEffect, useRef, useMemo } from 'react';
import Plotly from 'plotly.js-dist';
import Globe from 'globe.gl';
import { scaleLinear } from 'd3-scale';
import * as THREE from 'three';

// Debounce utility function with cancel method
const debounce = (func, wait) => {
  let timeout;
  const debounced = (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
  debounced.cancel = () => {
    clearTimeout(timeout);
  };
  return debounced;
};

// Helper to convert number to superscript for scientific notation
const toSuperscript = (num) => {
  const superscripts = {
    '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴',
    '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹',
    '-': '⁻'
  };
  return String(num).split('').map(char => superscripts[char] || char).join('');
};

// Smart number formatter for legend values - handles all scales robustly (abbreviated format)
const formatLegendValue = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return 'N/A';
  }
  
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  
  // Handle zero
  if (absValue === 0) {
    return '0';
  }
  
  // Very small numbers (less than 0.001) - use scientific notation
  if (absValue > 0 && absValue < 0.001) {
    const exp = Math.floor(Math.log10(absValue));
    const mantissa = absValue / Math.pow(10, exp);
    return `${sign}${mantissa.toFixed(1)}×10${toSuperscript(exp)}`;
  }
  
  // Small decimals (0.001 to 1)
  if (absValue < 1) {
    // Determine appropriate decimal places based on magnitude
    if (absValue < 0.01) return value.toFixed(4);
    if (absValue < 0.1) return value.toFixed(3);
    return value.toFixed(2);
  }
  
  // Regular numbers (1 to 999)
  if (absValue < 1000) {
    // Remove unnecessary decimals for whole numbers
    return absValue === Math.floor(absValue) ? value.toString() : value.toFixed(1);
  }
  
  // Thousands (1K to 999K)
  if (absValue < 1e6) {
    const val = absValue / 1e3;
    return `${sign}${val >= 100 ? val.toFixed(0) : val.toFixed(1)}K`;
  }
  
  // Millions (1M to 999M)
  if (absValue < 1e9) {
    const val = absValue / 1e6;
    return `${sign}${val >= 100 ? val.toFixed(0) : val.toFixed(1)}M`;
  }
  
  // Billions (1B to 999B)
  if (absValue < 1e12) {
    const val = absValue / 1e9;
    return `${sign}${val >= 100 ? val.toFixed(0) : val.toFixed(1)}B`;
  }
  
  // Trillions (1T to 999T)
  if (absValue < 1e15) {
    const val = absValue / 1e12;
    return `${sign}${val >= 100 ? val.toFixed(0) : val.toFixed(1)}T`;
  }
  
  // Very large numbers - use scientific notation
  const exp = Math.floor(Math.log10(absValue));
  const mantissa = absValue / Math.pow(10, exp);
  return `${sign}${mantissa.toFixed(1)}×10${toSuperscript(exp)}`;
};

// Detailed number formatter for hover values - shows full numbers with commas
const formatHoverValue = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return 'N/A';
  }
  
  const absValue = Math.abs(value);
  
  // Handle zero
  if (absValue === 0) {
    return '0';
  }
  
  // Very small numbers (less than 0.001) - use scientific notation
  if (absValue > 0 && absValue < 0.001) {
    const exp = Math.floor(Math.log10(absValue));
    const mantissa = absValue / Math.pow(10, exp);
    return `${value < 0 ? '-' : ''}${mantissa.toFixed(2)}×10${toSuperscript(exp)}`;
  }
  
  // Small decimals (0.001 to 1)
  if (absValue < 1) {
    if (absValue < 0.01) return value.toFixed(4);
    if (absValue < 0.1) return value.toFixed(3);
    return value.toFixed(2);
  }
  
  // Numbers less than 10 - show 2 decimal places
  if (absValue < 10) {
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  
  // Numbers less than 100 - show 1 decimal place
  if (absValue < 100) {
    return value.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  }
  
  // Numbers less than 1 million - show with commas, no decimals
  if (absValue < 1e6) {
    return Math.round(value).toLocaleString('en-US');
  }
  
  // Numbers less than 1 billion - show with commas
  if (absValue < 1e9) {
    return Math.round(value).toLocaleString('en-US');
  }
  
  // Numbers 1 billion and above - use abbreviated format with more precision
  if (absValue < 1e12) {
    const val = value / 1e9;
    return `${val.toFixed(2)}B`;
  }
  
  if (absValue < 1e15) {
    const val = value / 1e12;
    return `${val.toFixed(2)}T`;
  }
  
  // Very large numbers - use scientific notation
  const exp = Math.floor(Math.log10(absValue));
  const mantissa = absValue / Math.pow(10, exp);
  return `${value < 0 ? '-' : ''}${mantissa.toFixed(2)}×10${toSuperscript(exp)}`;
};

// Helper function to check if a variable is in the non-binary exceptions list
const isNonBinaryException = (variable) => {
  if (!variable) return false;
  
  const nonBinaryKeywords = [
    'cancer control score',
    'national cancer control'
  ];
  
  const normalizedVariable = variable.trim().toLowerCase();
  
  const isException = nonBinaryKeywords.some(keyword => 
    normalizedVariable.includes(keyword.toLowerCase())
  );
  
  console.log('isNonBinaryException check:', { 
    variable, 
    normalizedVariable, 
    isException 
  });
  
  return isException;
};

// Helper function to check if variable should use static 0-5 range
const usesStaticScoreRange = (variable) => {
  if (!variable) return false;
  
  const normalizedVariable = variable.trim().toLowerCase();
  
  const staticRangeKeywords = [
    'cancer control score',
    'national cancer control',
    'nccp score'
  ];
  
  const result = staticRangeKeywords.some(keyword => 
    normalizedVariable.includes(keyword.toLowerCase())
  );
  
  console.log('usesStaticScoreRange check:', { 
    variable, 
    normalizedVariable, 
    result 
  });
  
  return result;
};

// Helper function to generate integer tick positions and labels
const generateIntegerTicks = (minValue, maxValue) => {
  const intMin = Math.floor(minValue);
  const intMax = Math.ceil(maxValue);
  
  const integerValues = [];
  for (let i = intMin; i <= intMax; i++) {
    integerValues.push(i);
  }
  
  const tickPositions = integerValues.map(val => {
    if (maxValue === minValue) return 50;
    return ((val - minValue) / (maxValue - minValue)) * 100;
  });
  
  const tickLabels = integerValues.map(val => val.toString());
  
  return { tickPositions, tickLabels };
};

// ============================================
// COLOR SCHEME HELPER FUNCTIONS
// ============================================

/**
 * Get the color scale based on colorScheme from MetaData.csv
 * 
 * Color Scheme values:
 * - 1 = Red to Green (higher values are BETTER - e.g., life expectancy, vaccination rates)
 * - 2 = Green to Red (lower values are BETTER - e.g., mortality rates, infant deaths)  
 * - 3 = Neutral (no value judgment - e.g., population, uses green gradient)
 */
const getColorScaleFromScheme = (colorScheme, isBinaryVariable = false) => {
  // Binary variables always use discrete red/green
  // For binary (Yes/No) data: 0 = Red (No), 1 = Green (Yes)
  if (isBinaryVariable) {
    return [
      [0, '#ff0000'],      // Red for 0 (No)
      [0.45, '#ff0000'],   // Red up to 45%
      [0.55, '#2cba00'],   // Green from 55%
      [1, '#2cba00']       // Green for 1 (Yes)
    ];
  }
  
  switch (colorScheme) {
    case 1:
      // Red to Green: Higher is BETTER (life expectancy, health coverage, etc.)
      // Low values = red (bad), high values = green (good)
      return [
        [0, '#ff0000'],     // Red for low values (worse)
        [0.25, '#ffa700'],  // Orange
        [0.5, '#fff400'],   // Yellow
        [0.75, '#a3ff00'],  // Light green
        [1, '#2cba00']      // Green for high values (better)
      ];
      
    case 2:
      // Green to Red: Lower is BETTER (mortality, deaths, disease rates, etc.)
      // Low values = green (good), high values = red (bad)
      return [
        [0, '#2cba00'],     // Green for low values (better)
        [0.25, '#a3ff00'],  // Light green
        [0.5, '#fff400'],   // Yellow
        [0.75, '#ffa700'],  // Orange
        [1, '#ff0000']      // Red for high values (worse)
      ];
      
    case 3:
    default:
      // Neutral: No value judgment (population, counts, etc.)
      // Uses a green gradient - no good/bad implied
      return [
        [0, '#e5f5e0'],     // Very light green
        [0.25, '#c7e9c0'],  // Light green
        [0.5, '#a1d99b'],   // Medium green
        [0.75, '#74c476'],  // Dark green
        [1, '#31a354']      // Very dark green
      ];
  }
};

/**
 * Get D3 color scale function for 3D globe based on colorScheme
 */
const getD3ColorScaleFromScheme = (colorScheme, isBinaryVariable = false) => {
  if (isBinaryVariable) {
    return (value) => value >= 50 ? '#2cba00' : '#ff0000';
  }
  
  switch (colorScheme) {
    case 1:
      // Red to Green: Higher is BETTER
      return scaleLinear()
        .domain([0, 25, 50, 75, 100])
        .range(['#ff0000', '#ffa700', '#fff400', '#a3ff00', '#2cba00']);
        
    case 2:
      // Green to Red: Lower is BETTER
      return scaleLinear()
        .domain([0, 25, 50, 75, 100])
        .range(['#2cba00', '#a3ff00', '#fff400', '#ffa700', '#ff0000']);
        
    case 3:
    default:
      // Neutral: Green gradient
      return scaleLinear()
        .domain([0, 25, 50, 75, 100])
        .range(['#e5f5e0', '#c7e9c0', '#a1d99b', '#74c476', '#31a354']);
  }
};

/**
 * Get legend label based on colorScheme
 */
const getLegendLabelFromScheme = (colorScheme, isBinaryVariable = false) => {
  if (isBinaryVariable) {
    return 'Yes/No';
  }
  
  switch (colorScheme) {
    case 1:
      return 'Low → High (Better)';
    case 2:
      return 'Low (Better) → High';
    case 3:
    default:
      return 'Low → High';
  }
};

// ============================================
// MAIN MAPVIEW COMPONENT
// ============================================

const MapView = ({ 
  filteredData, 
  countryMapping, 
  m49Mapping, 
  worldGeoJSON, 
  selectedYear, 
  setSelectedYear, 
  mapMode, 
  setMapMode, 
  isSidebarOpen, 
  isRightPanelOpen, 
  dataStructure, 
  selectedVariable, 
  selectedMeasure, 
  stopAnimation, 
  legendBinary, 
  legendMap = {}, 
  variableDefinitions = {}, 
  visualizationType = 2,
  colorScheme = 3,
  isBinaryVariable = false  // From "Binary" column in MetaData.csv
}) => {
  const [mapError, setMapError] = useState(null);
  const [minValue, setMinValue] = useState(0);
  const [maxValue, setMaxValue] = useState(100);
  const [mapKey, setMapKey] = useState(0);
  const [mapDimensions, setMapDimensions] = useState({ width: 800, height: 500 });
  const [isAnimatingYear, setIsAnimatingYear] = useState(false);

  const mapContainerRef = useRef(null);
  const globeContainerRef = useRef(null);
  const plotlyMapRef = useRef(null);
  const globeRef = useRef(null);
  const animationIntervalRef = useRef(null);
  const debugRef = useRef({
    mapLoaded: false,
    errors: []
  });

  const handleGlobeClickRef = useRef(null);
  const handleDragEndRef = useRef(null);
  const handlePointerUpRef = useRef(null);
  const handleResizeRef = useRef(null);

  // Font loading utility
  const ensureFontLoaded = () => {
    return new Promise((resolve) => {
      if (document.fonts && document.fonts.check) {
        if (document.fonts.check('300 16px Calibre-Light')) {
          resolve();
        } else {
          document.fonts.ready.then(() => {
            setTimeout(resolve, 100);
          });
        }
      } else {
        setTimeout(resolve, 500);
      }
    });
  };

  // Custom download function with watermarks for 2D map
  const downloadMapWithWatermark = async () => {
    if (!plotlyMapRef.current || !plotlyMapRef.current._fullLayout || mapMode !== '2d') return;
    
    try {
      await ensureFontLoaded();
      
      const currentLayout = plotlyMapRef.current.layout;
      const currentData = plotlyMapRef.current.data;
      
      const watermarkedLayout = {
        ...currentLayout,
        images: [
          {
            source: '/logo.png',
            xref: 'paper',
            yref: 'paper',
            x: 0.98,
            y: 0.02,
            sizex: 0.12,
            sizey: 0.12,
            xanchor: 'right',
            yanchor: 'bottom',
            opacity: 0.9,
            layer: 'above'
          }
        ],
        annotations: [
          ...(currentLayout.annotations || []),
          {
            text: '© Health Systems Innovation Lab, Harvard University, 2025',
            xref: 'paper',
            yref: 'paper',
            x: 0.01,
            y: 0.01,
            xanchor: 'left',
            yanchor: 'bottom',
            showarrow: false,
            font: {
              size: 11,
              color: 'rgba(0, 0, 0, 0.9)',
              family: 'Calibre-Light, Arial, sans-serif',
              weight: 'bold'
            },
            bgcolor: 'rgba(255, 255, 255, 0.7)',
            borderwidth: 0,
            borderpad: 6
          }
        ],
        font: {
          family: 'Calibre-Light, Arial, sans-serif',
          size: 14
        }
      };
      
      await Plotly.react(plotlyMapRef.current, currentData, watermarkedLayout);
      
      await Plotly.downloadImage(plotlyMapRef.current, {
        format: 'png',
        filename: `map_${selectedVariable || 'data'}_${selectedYear || 'all'}`,
        height: mapDimensions.height,
        width: mapDimensions.width,
        scale: 2
      });
      
      await Plotly.react(plotlyMapRef.current, currentData, currentLayout);
      
    } catch (error) {
      console.error('Error downloading map with watermark:', error);
    }
  };

  // Custom download function for 3D globe
  const downloadGlobeWithWatermark = async () => {
    if (!globeRef.current || mapMode !== '3d') return;
    
    try {
      const globeCanvas = globeRef.current.renderer().domElement;
      
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = globeCanvas.width;
      finalCanvas.height = globeCanvas.height;
      const ctx = finalCanvas.getContext('2d');
      
      ctx.drawImage(globeCanvas, 0, 0);
      
      ctx.font = '22px Calibre-Light, Arial, sans-serif';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.fillRect(10, finalCanvas.height - 40, 800, 30);
      ctx.fillStyle = 'rgba(0, 0, 0, 0.9)';
      ctx.fillText('© Health Systems Innovation Lab, Harvard University, 2025', 20, finalCanvas.height - 20);
      
      const logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';
      logoImg.onload = () => {
        const logoWidth = 120;
        const logoHeight = 120;
        const logoX = finalCanvas.width - logoWidth - 20;
        const logoY = finalCanvas.height - logoHeight - 20;
        
        ctx.drawImage(logoImg, logoX, logoY, logoWidth, logoHeight);
        
        finalCanvas.toBlob(blob => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `globe_${selectedVariable || 'data'}_${selectedYear || 'all'}.png`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        });
      };
      logoImg.src = '/logo.png';
      
    } catch (error) {
      console.error('Error downloading globe with watermark:', error);
    }
  };

  // Memoize available years from filtered data
  const availableYears = useMemo(() => {
    if (!filteredData || filteredData.length === 0) {
      return dataStructure.years || [];
    }
    
    const yearsFromData = [...new Set(filteredData.map(row => row.Year))]
      .filter(year => year && Number.isInteger(year))
      .sort((a, b) => a - b);
    
    console.log('Available years from filtered data:', yearsFromData);
    return yearsFromData;
  }, [filteredData, dataStructure.years]);

  // Update selectedYear when availableYears change
  useEffect(() => {
    if (availableYears.length > 0) {
      const firstYear = availableYears[0];
      if (selectedYear !== firstYear) {
        setSelectedYear(firstYear);
        console.log(`Updated selectedYear from ${selectedYear} to ${firstYear} (first available year)`);
      }
    }
  }, [availableYears]);

  // Memoize countryData for Plotly choropleth map and 3D globe
  // *** FIXED: Added isBinaryVariable to handle binary data normalization correctly ***
  const countryData = useMemo(() => {
    if (!worldGeoJSON) {
      console.log('Cannot generate country data: worldGeoJSON is missing');
      return { locations: [], z: [], text: [], raw: {}, normalizedZ: [] };
    }

    const locations = [];
    const z = [];
    const normalizedZ = [];
    const text = [];
    const raw = {};
    let min = Infinity;
    let max = -Infinity;

    const yearData = filteredData.length > 0 && selectedYear ? filteredData.filter(row => row.Year === selectedYear) : [];
    console.log(`Data for year ${selectedYear}:`, yearData);

    const countriesByName = {};
    yearData.forEach(row => {
      if (row.Country && typeof row.Value === 'number' && !isNaN(row.Value)) {
        countriesByName[row.Country] = row.Value;
        if (row.Value < min) min = row.Value;
        if (row.Value > max) max = row.Value;
      }
    });

    // *** FIX: For binary variables, force min=0 and max=1 regardless of actual data ***
    if (isBinaryVariable) {
      min = 0;
      max = 1;
      console.log('Binary variable detected, using fixed min=0, max=1');
    }

    const reversedM49 = Object.fromEntries(Object.entries(m49Mapping).map(([k, v]) => [v, k]));
    const nameVariations = {};
    const commonFixes = {
      "United States": ["USA", "United States of America", "US"],
      "United Kingdom": ["UK", "Great Britain", "England", "GBR"],
      "Russia": ["Russian Federation", "RUS"],
      "South Korea": ["Korea", "Korea, Republic of", "KOR"],
      "North Korea": ["Korea, Democratic People's Republic of", "PRK"],
      "Fiji": ["FJI"]
    };

    Object.entries(countriesByName).forEach(([countryName, value]) => {
      nameVariations[countryName] = value;
      if (countryMapping[countryName]) {
        nameVariations[countryMapping[countryName]] = value;
      }
      if (m49Mapping[countryName]) {
        nameVariations[m49Mapping[countryName]] = value;
        raw[m49Mapping[countryName]] = value;
      }
      Object.entries(commonFixes).forEach(([standard, variations]) => {
        if (countryName === standard) {
          variations.forEach(variant => {
            nameVariations[variant] = value;
          });
        }
        if (variations.includes(countryName)) {
          nameVariations[standard] = value;
          if (countryMapping[standard]) {
            nameVariations[countryMapping[standard]] = value;
          }
          if (m49Mapping[standard]) {
            nameVariations[m49Mapping[standard]] = value;
            raw[m49Mapping[standard]] = value;
          }
        }
      });
    });

    if (worldGeoJSON.features) {
      worldGeoJSON.features.forEach(feature => {
        let matched = false;
        let featureId = feature.id;
        const countryName = feature.properties?.name || reversedM49[featureId] || `Country ${featureId}`;
        let value;

        if (featureId && nameVariations[featureId] !== undefined) {
          value = nameVariations[featureId];
          matched = true;
        }

        if (!matched && featureId && reversedM49[featureId]) {
          const countryNameFromM49 = reversedM49[featureId];
          if (countryNameFromM49 && nameVariations[countryNameFromM49] !== undefined) {
            value = nameVariations[countryNameFromM49];
            matched = true;
          }
        }

        if (!matched && feature.properties) {
          const properties = feature.properties;
          const possibleMatches = [
            properties.ISO_A3,
            properties.iso_a3,
            properties.ISO_A2,
            properties.iso_a2,
            properties.ADM0_A3,
            properties.adm0_a3,
            properties.name,
            properties.NAME,
            properties.NAME_LONG,
            properties.name_long,
            properties.ADMIN,
            properties.admin,
            properties.FORMAL_EN,
            properties.formal_en
          ].filter(Boolean);

          for (const id of possibleMatches) {
            if (id && nameVariations[id] !== undefined) {
              value = nameVariations[id];
              matched = true;
              break;
            }
          }
        }

        if (matched && value !== undefined) {
          raw[featureId] = value;
          raw[countryName] = value;
          if (feature.properties?.ISO_A3) {
            raw[feature.properties.ISO_A3] = value;
            locations.push(feature.properties.ISO_A3);
            z.push(value);
            
            // *** KEY FIX: Handle binary variables with fixed normalization ***
            let normalizedValue;
            if (isBinaryVariable) {
              // For binary (Yes/No) variables: 0 → 0% (red), 1 → 100% (green)
              normalizedValue = value === 1 ? 100 : 0;
            } else {
              // For continuous variables, normalize based on actual data range
              normalizedValue = max !== min ? ((value - min) / (max - min)) * 100 : 50;
            }
            normalizedZ.push(normalizedValue);
            
            text.push(`${countryName}: ${formatHoverValue(value)} (${selectedYear || 'No year'})`);
          }
          console.log('Matched country:', { countryName, featureId, value, iso_a3: feature.properties?.ISO_A3 });
        } else {
          console.log('Unmatched country:', { countryName, featureId, iso_a3: feature.properties?.ISO_A3 });
        }
      });

      Object.entries(countriesByName).forEach(([countryName, value]) => {
        if (countryMapping[countryName] && !locations.includes(countryMapping[countryName])) {
          locations.push(countryMapping[countryName]);
          z.push(value);
          
          // *** SAME FIX HERE ***
          let normalizedValue;
          if (isBinaryVariable) {
            normalizedValue = value === 1 ? 100 : 0;
          } else {
            normalizedValue = max !== min ? ((value - min) / (max - min)) * 100 : 50;
          }
          normalizedZ.push(normalizedValue);
          
          text.push(`${countryName}: ${formatHoverValue(value)} (${selectedYear || 'No year'})`);
          raw[countryMapping[countryName]] = value;
          raw[countryName] = value;
          console.log('Added country via countryMapping:', { countryName, iso_a3: countryMapping[countryName], value });
        }
      });
    }

    const computedMinValue = min !== Infinity ? min : 0;
    let computedMaxValue = max !== -Infinity ? max : 100;
    if (computedMinValue === computedMaxValue) {
      computedMaxValue = computedMinValue + 1;
    }
    setMinValue(computedMinValue);
    setMaxValue(computedMaxValue);

    console.log('Country data:', { 
      locations, 
      z, 
      normalizedZ, 
      text, 
      raw, 
      minValue: computedMinValue, 
      maxValue: computedMaxValue,
      isBinaryVariable 
    });
    return { locations, z, normalizedZ, text, raw };
  }, [filteredData, countryMapping, m49Mapping, worldGeoJSON, selectedYear, isBinaryVariable]); // *** Added isBinaryVariable ***

  // Log filteredData for debugging
  useEffect(() => {
    console.log('MapView received filteredData:', filteredData);
    console.log('filteredData length:', filteredData.length);
    console.log('Sample data:', filteredData.slice(0, 5));
    console.log('Map mode:', mapMode);
    console.log('Legend binary:', legendBinary);
    console.log('Selected variable:', selectedVariable);
    console.log('Selected measure:', selectedMeasure);
    console.log('Legend map:', legendMap);
    console.log('Visualization type:', visualizationType);
    console.log('Color scheme from metadata:', colorScheme);
    console.log('isBinaryVariable:', isBinaryVariable);
    console.log('Is non-binary exception:', isNonBinaryException(selectedVariable));
    console.log('Uses static score range:', usesStaticScoreRange(selectedVariable));
  }, [filteredData, mapMode, legendBinary, selectedVariable, selectedMeasure, legendMap, visualizationType, colorScheme, isBinaryVariable]);

  // Handle resize for window and sidebar/panel changes
  useEffect(() => {
    const handleResize = () => {
      if (mapContainerRef.current) {
        const containerRect = mapContainerRef.current.getBoundingClientRect();
        const containerWidth = Math.max(containerRect.width - 40, 400);
        const containerHeight = Math.max(containerRect.height - 80, 300);

        setMapDimensions({
          width: containerWidth,
          height: containerHeight
        });

        if (mapMode === '2d' && plotlyMapRef.current && window.Plotly) {
          const plotElement = plotlyMapRef.current;
          if (plotElement._fullLayout && plotElement.data) {
            setTimeout(() => {
              try {
                Plotly.Plots.resize(plotElement);
                console.log('Plotly resized due to panel change');
              } catch (error) {
                console.warn('Resize failed:', error);
              }
            }, 150);
          }
        }

        if (mapMode === '3d' && globeRef.current) {
          globeRef.current.width(containerWidth).height(containerHeight);
        }

        console.log('Resizing map to:', { 
          width: containerWidth, 
          height: containerHeight, 
          containerRect,
          mapMode
        });
      }
    };

    const debouncedResize = debounce(handleResize, 100);
    setTimeout(handleResize, 100);
    window.addEventListener('resize', debouncedResize);

    return () => {
      window.removeEventListener('resize', debouncedResize);
      debouncedResize.cancel();
    };
  }, [isSidebarOpen, isRightPanelOpen, mapMode]);

  // Re-render map when dimensions change
  useEffect(() => {
    if (mapMode === '2d' && plotlyMapRef.current && window.Plotly && mapDimensions.width > 0) {
      const plotElement = plotlyMapRef.current;
      if (!plotElement._fullLayout || !plotElement.data) {
        console.log('Plot not fully initialized, skipping relayout');
        return;
      }

      console.log('Updating map layout due to dimension change:', mapDimensions);
      
      const update = {
        width: mapDimensions.width,
        height: mapDimensions.height
      };

      setTimeout(() => {
        if (plotElement._fullLayout && plotElement.data) {
          Plotly.relayout(plotElement, update).then(() => {
            console.log('Map layout updated successfully');
          }).catch(error => {
            console.warn('Error updating map layout, falling back to resize:', error);
            try {
              Plotly.Plots.resize(plotElement);
            } catch (resizeError) {
              console.error('Resize also failed:', resizeError);
            }
          });
        }
      }, 200);
    }
  }, [mapDimensions, mapMode]);

  // Force re-render only when switching to 2D mode
  useEffect(() => {
    if (mapMode === '2d') {
      setMapKey(prevKey => prevKey + 1);
      console.log('Switched to 2D mode, forcing re-render with mapKey:', mapKey + 1);
    }
  }, [mapMode]);

  // Check worldGeoJSON
  useEffect(() => {
    if (worldGeoJSON) {
      debugRef.current.mapLoaded = true;
      console.log('World map loaded with', worldGeoJSON.features.length, 'features');
    } else {
      console.warn('No worldGeoJSON provided to MapView');
      setMapError('No map data available');
    }
  }, [worldGeoJSON]);

  // ============================================
  // RENDER PLOTLY 2D CHOROPLETH MAP
  // ============================================
  useEffect(() => {
    if (mapMode !== '2d' || !worldGeoJSON || !plotlyMapRef.current) return;

    const mapRef = plotlyMapRef.current;

    // Check if this is a binary variable
    // isBinaryVariable is passed as a prop from OECDExplorer, derived from the "Binary" column in MetaData.csv
    const isException = isNonBinaryException(selectedVariable);
    const effectiveIsBinary = isBinaryVariable && !isException;
    
    // Check if this variable should use static 0-5 range
    const useStaticRange = usesStaticScoreRange(selectedVariable);
    
    // Determine the legend min/max values
    const legendMinValue = useStaticRange ? 0 : minValue;
    const legendMaxValue = useStaticRange ? 5 : maxValue;
    
    console.log('Color scale determination (2D map):', {
      selectedVariable,
      colorScheme,
      visualizationType,
      isException,
      effectiveIsBinary,
      isBinaryVariable,
      useStaticRange,
      legendMinValue,
      legendMaxValue,
      actualMinValue: minValue,
      actualMaxValue: maxValue
    });
    
    // Get colorscale based on colorScheme from metadata
    let colorscale = getColorScaleFromScheme(colorScheme, effectiveIsBinary);
    
    // Override for special cases
    if (isException || useStaticRange) {
      // Exception variables like National Cancer Control Scores: use gradient with integer ticks
      // Higher is better for these scores
      colorscale = getColorScaleFromScheme(1, false); // Use red-to-green
    }
    
    let tickPositions;
    let tickLabels;
    
    // For static range variables, recalculate normalizedZ based on 0-5 scale
    let displayNormalizedZ = countryData.normalizedZ;
    if (useStaticRange && countryData.z.length > 0) {
      displayNormalizedZ = countryData.z.map(val => {
        if (val === null || val === undefined || isNaN(val)) return 0;
        const clampedVal = Math.max(0, Math.min(5, val));
        return (clampedVal / 5) * 100;
      });
      console.log('Recalculated normalizedZ for static 0-5 range:', displayNormalizedZ.slice(0, 5));
    }
    
    if (effectiveIsBinary) {
      tickPositions = [0, 100];
      tickLabels = ['0 (No)', '1 (Yes)'];
    } else if (isException || useStaticRange) {
      const integerTicks = generateIntegerTicks(legendMinValue, legendMaxValue);
      tickPositions = integerTicks.tickPositions;
      tickLabels = integerTicks.tickLabels;
    } else {
      tickPositions = [0, 25, 50, 75, 100];
      tickLabels = tickPositions.map(pos => {
        const actualValue = legendMinValue + (legendMaxValue - legendMinValue) * (pos / 100);
        return formatLegendValue(actualValue);
      });
    }

    const data = [
      {
        type: 'choropleth',
        locations: countryData.locations.length > 0 ? countryData.locations : [],
        z: displayNormalizedZ.length > 0 ? displayNormalizedZ : [],
        text: countryData.text.length > 0 ? countryData.text : [],
        colorscale,
        zmin: 0,
        zmax: 100,
        marker: {
          line: {
            color: '#D6D6DA',
            width: 0.5
          }
        },
        colorbar: {
          title: {
            text: effectiveIsBinary ? 'Yes/No' : (selectedMeasure || 'Value'),
            font: {
              size: 11,
              family: 'Arial, sans-serif'
            }
          },
          titleside: 'top',
          x: 0.5,
          y: -0.15,
          xanchor: 'center',
          yanchor: 'top',
          len: 0.5,
          thickness: 12,
          orientation: 'h',
          tickvals: tickPositions,
          ticktext: tickLabels,
          tickfont: {
            size: 9,
            family: 'Arial, sans-serif'
          },
          tickangle: 0,
          outlinewidth: 0,
          borderwidth: 0
        },
        hoverinfo: 'text'
      }
    ];

    const layout = {
      geo: {
        scope: 'world',
        projection: {
          type: 'natural earth'
        },
        showframe: false,
        showcoastlines: true,
        coastlinecolor: '#D6D6DA',
        showland: true,
        landcolor: '#F5F5F5',
        showocean: true,
        oceancolor: '#1e3a5f',
        showrivers: false,
        showlakes: true,
        lakecolor: '#2563eb',
        showcountries: true,
        countrycolor: '#D6D6DA'
      },
      width: mapDimensions.width,
      height: mapDimensions.height,
      margin: {
        l: 40,
        r: 40,
        t: 40,
        b: 120
      },
      annotations: [],
      paper_bgcolor: 'white',
      plot_bgcolor: 'white'
    };

    const config = {
      responsive: true,
      displayModeBar: false,
      displaylogo: false,
      toImageButtonOptions: {
        format: 'png',
        filename: `map_${selectedYear || 'data'}`,
        height: mapDimensions.height,
        width: mapDimensions.width,
        scale: 2
      }
    };

    try {
      Plotly.newPlot(mapRef, data, layout, config);
      console.log('Plotly 2D choropleth map rendered successfully', { 
        locationsLength: countryData.locations.length,
        colorScheme,
        legendMinValue,
        legendMaxValue,
        tickLabels,
        effectiveIsBinary,
        isException,
        useStaticRange
      });
    } catch (error) {
      console.error('Error rendering Plotly map:', error);
      setMapError('Failed to render map: ' + error.message);
      debugRef.current.errors.push({
        type: 'plotly',
        message: error.message,
        timestamp: new Date().toISOString()
      });
    }

    return () => {
      if (mapRef) {
        Plotly.purge(mapRef);
      }
    };
  }, [mapMode, worldGeoJSON, countryData, mapDimensions, minValue, maxValue, mapKey, filteredData, selectedVariable, selectedMeasure, selectedYear, legendMap, visualizationType, colorScheme, isBinaryVariable]);

  // Initialize 3D globe ONLY once when switching to 3D mode
  useEffect(() => {
    if (mapMode !== '3d' || !worldGeoJSON || !globeContainerRef.current) return;

    if (globeRef.current) {
      console.log('Globe already exists, skipping initialization');
      return;
    }

    const globeContainer = globeContainerRef.current;
    console.log('Initializing new 3D globe');

    const globe = Globe()(globeContainer)
      .globeImageUrl(null)
      .backgroundColor('rgba(255, 255, 255, 0)')
      .showGlobe(true)
      .globeMaterial(new THREE.MeshLambertMaterial({ color: '#1e3a5f', transparent: true, opacity: 0.9 }))
      .polygonsData(worldGeoJSON.features)
      .polygonCapColor(() => '#F5F5F5')
      .polygonSideColor(() => '#D6D6DA')
      .polygonStrokeColor(() => '#D6D6DA')
      .polygonLabel(() => '')
      .onPolygonHover((hoverD) => {
        if (hoverD) {
          globe.polygonAltitude(d => d === hoverD ? 0.1 : 0.05);
        } else {
          globe.polygonAltitude(0.05);
        }
      });

    globeRef.current = globe;

    const controls = globe.controls();
    controls.autoRotate = true;
    controls.autoRotateSpeed = 1;

    handleGlobeClickRef.current = () => {
      console.log('Globe clicked');
    };

    handleDragEndRef.current = () => {
      console.log('Globe drag ended');
    };

    handlePointerUpRef.current = () => {
      console.log('Pointer up on globe');
    };

    handleResizeRef.current = () => {
      if (globeContainer && mapContainerRef.current) {
        const containerRect = mapContainerRef.current.getBoundingClientRect();
        globe.width(containerRect.width).height(containerRect.height - 60);
      }
    };

    globe.onGlobeClick(handleGlobeClickRef.current);
    controls.addEventListener('end', handleDragEndRef.current);
    globeContainer.addEventListener('pointerup', handlePointerUpRef.current);
    window.addEventListener('resize', handleResizeRef.current);

    handleResizeRef.current();

    return () => {
      console.log('Cleaning up globe initialization');
      if (globe) {
        globe.onGlobeClick(null);
        globe.onPolygonHover(null);
      }
      if (controls) {
        controls.removeEventListener('end', handleDragEndRef.current);
      }
      if (globeContainer) {
        globeContainer.removeEventListener('pointerup', handlePointerUpRef.current);
      }
      window.removeEventListener('resize', handleResizeRef.current);
      
      globeRef.current = null;
    };
  }, [mapMode, worldGeoJSON, mapDimensions, isSidebarOpen, isRightPanelOpen]);

  // ============================================
  // UPDATE GLOBE COLORS AND LABELS WHEN DATA CHANGES
  // ============================================
  useEffect(() => {
    if (!globeRef.current || mapMode !== '3d' || !worldGeoJSON || !countryData) {
      return;
    }

    console.log('Updating globe colors and labels for year:', selectedYear, { minValue, maxValue, colorScheme, isBinaryVariable });
    
    // Check if this is a binary variable
    const isException = isNonBinaryException(selectedVariable);
    const effectiveIsBinary = isBinaryVariable && !isException;
    
    // Check if this variable should use static 0-5 range
    const useStaticRange = usesStaticScoreRange(selectedVariable);
    
    // Determine the legend min/max values for 3D globe
    const legendMinValue = useStaticRange ? 0 : minValue;
    const legendMaxValue = useStaticRange ? 5 : maxValue;
    
    // Get D3 color scale based on colorScheme from metadata
    let colorScale = getD3ColorScaleFromScheme(colorScheme, effectiveIsBinary);
    
    // Override for special cases
    if (isException || useStaticRange) {
      colorScale = getD3ColorScaleFromScheme(1, false); // Higher is better
    }

    const getColorForFeature = (feature) => {
      const featureId = feature.id ? String(feature.id) : null;
      let value = featureId ? countryData.raw[featureId] : undefined;
      
      if (value === undefined && feature.properties) {
        const props = feature.properties;
        const possibleIds = [
          props.ISO_A3,
          props.iso_a3,
          props.ISO_A2,
          props.iso_a2,
          props.ADM0_A3,
          props.adm0_a3,
          props.name,
          props.NAME,
          props.NAME_LONG,
          props.name_long,
          props.ADMIN,
          props.admin,
          props.FORMAL_EN,
          props.formal_en
        ].filter(Boolean);
        
        for (const id of possibleIds) {
          if (countryData.raw[id] !== undefined) {
            value = countryData.raw[id];
            break;
          }
        }
      }
      
      let normalizedValue;
      if (effectiveIsBinary && value !== undefined) {
        // *** FIX FOR 3D GLOBE: Binary variables use fixed normalization ***
        normalizedValue = value === 1 ? 100 : 0;
      } else if (useStaticRange && value !== undefined) {
        const clampedVal = Math.max(0, Math.min(5, value));
        normalizedValue = (clampedVal / 5) * 100;
      } else if (value !== undefined && legendMaxValue !== legendMinValue) {
        normalizedValue = ((value - legendMinValue) / (legendMaxValue - legendMinValue)) * 100;
      } else {
        normalizedValue = value !== undefined ? 50 : undefined;
      }
      
      return normalizedValue !== undefined ? colorScale(normalizedValue) : '#F5F5F5';
    };

    const getLabelForFeature = (feature) => {
      const featureId = feature.id ? String(feature.id) : null;
      const reversedM49 = Object.fromEntries(Object.entries(m49Mapping).map(([k, v]) => [v, k]));
      const countryName = feature.properties?.name || (featureId && reversedM49[featureId]) || `Country ${featureId || 'unknown'}`;
      
      let value = featureId ? countryData.raw[featureId] : undefined;
      if (value === undefined && feature.properties) {
        const props = feature.properties;
        const possibleIds = [
          props.ISO_A3, props.iso_a3, props.ISO_A2, props.iso_a2,
          props.ADM0_A3, props.adm0_a3, props.name, props.NAME,
          props.NAME_LONG, props.name_long, props.ADMIN, props.admin,
          props.FORMAL_EN, props.formal_en
        ].filter(Boolean);
        
        for (const id of possibleIds) {
          if (countryData.raw[id] !== undefined) {
            value = countryData.raw[id];
            break;
          }
        }
      }
      
      const displayValue = typeof value === 'number' && !isNaN(value) 
        ? formatHoverValue(value)
        : 'No data';
        
      return `
        <div class="map-tooltip">
          ${countryName}: ${displayValue} (${selectedYear || 'No year'})
        </div>
      `;
    };

    globeRef.current
      .polygonCapColor(getColorForFeature)
      .polygonLabel(getLabelForFeature);

    console.log('Globe colors and labels updated successfully');
  }, [countryData, selectedYear, minValue, maxValue, m49Mapping, mapMode, worldGeoJSON, selectedVariable, selectedMeasure, legendMap, visualizationType, colorScheme, isBinaryVariable]);

  return (
    <div 
      className="map-view-container" 
      ref={mapContainerRef} 
      style={{ 
        flex: 1, 
        width: '100%', 
        height: '100%', 
        position: 'relative',
        minHeight: '400px',
        display: 'flex',
        flexDirection: 'column'
      }}
      data-mode={mapMode}
    >
      <div className="map-visualization-container" style={{ 
        width: '100%', 
        flex: 1,
        display: 'flex', 
        position: 'relative',
        minHeight: '340px'
      }}>
        {mapError ? (
          <div className="map-error">
            <p>Error loading map: {mapError}</p>
            <button 
              className="button button-secondary"
              onClick={() => console.log("Map debug info:", debugRef.current)}
            >
              Debug in Console
            </button>
          </div>
        ) : worldGeoJSON ? (
          <>
            {mapMode === '2d' && (
              <div 
                ref={plotlyMapRef} 
                className="plotly-map"
                style={{ 
                  width: '100%', 
                  height: '100%',
                  minHeight: '340px'
                }}
              >
                {countryData.locations.length === 0 && (
                  <div className="map-data-status">
                    <p>No country data available for year {selectedYear || 'N/A'}</p>
                    <p>Try different filters</p>
                  </div>
                )}
              </div>
            )}
            {mapMode === '3d' && (
              <div 
                ref={globeContainerRef} 
                className="globe-container"
                style={{ 
                  width: '100%', 
                  height: '100%',
                  minHeight: '340px'
                }}
              />
            )}
            
            {/* Custom download button for both 2D and 3D modes */}
            {selectedVariable && selectedMeasure && (
              <button
                onClick={mapMode === '2d' ? downloadMapWithWatermark : downloadGlobeWithWatermark}
                style={{
                  position: 'absolute',
                  top: '20px',
                  right: '20px',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  border: '1px solid #E5E7EB',
                  borderRadius: '4px',
                  padding: '6px 12px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  color: '#374151',
                  zIndex: 1000,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontFamily: 'Calibre-Light, Arial, sans-serif',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgba(249, 250, 251, 1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                }}
                title={`Download ${mapMode === '2d' ? 'map' : 'globe'} as PNG with watermark`}
              >
                <i className="fas fa-download" style={{ fontSize: '10px' }}></i>
                PNG
              </button>
            )}
          </>
        ) : (
          <div className="map-placeholder">
            <p>World Map Visualization</p>
            <p>(Map loading...)</p>
          </div>
        )}
      </div>
      
      <div className="year-slider-container" style={{ 
        position: 'absolute',
        bottom: '10px',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        border: '1px solid #e0e0e0',
        borderRadius: '20px',
        padding: '8px 16px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        zIndex: 1000,
        width: 'auto',
        minWidth: '300px',
        maxWidth: '400px'
      }}>
        <button
          className="play-pause-button"
          onClick={() => {
            if (isAnimatingYear) {
              stopAnimation();
              setIsAnimatingYear(false);
              clearInterval(animationIntervalRef.current);
              animationIntervalRef.current = null;
            } else {
              if (!availableYears.length) return;
              setIsAnimatingYear(true);
              let index = availableYears.indexOf(selectedYear) + 1;
              if (index >= availableYears.length) index = 0;
              animationIntervalRef.current = setInterval(() => {
                if (index >= availableYears.length) index = 0;
                setSelectedYear(availableYears[index]);
                index++;
              }, 1000);
            }
          }}
          disabled={!selectedMeasure || availableYears.length < 2}
          title={isAnimatingYear ? 'Pause' : 'Play'}
          style={{
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            border: '1px solid #ccc',
            backgroundColor: '#fff',
            cursor: 'pointer',
            fontSize: '10px',
            marginRight: '8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {isAnimatingYear ? '❚❚' : '▶'}
        </button>
        <input
          type="range"
          min={availableYears.length > 0 ? availableYears[0] : 2010}
          max={availableYears.length > 0 ? availableYears[availableYears.length - 1] : 2030}
          step="1"
          value={availableYears.length > 0 && availableYears.includes(selectedYear) ? selectedYear : (availableYears[0] || 2010)}
          onChange={e => {
            const newYear = Number(e.target.value);
            if (availableYears.length > 0 && availableYears.includes(newYear)) {
              setSelectedYear(newYear);
            } else if (availableYears.length > 0) {
              const closest = availableYears.reduce((prev, curr) => 
                Math.abs(curr - newYear) < Math.abs(prev - newYear) ? curr : prev
              );
              setSelectedYear(closest);
            }
            
            if (isAnimatingYear) {
              stopAnimation();
              setIsAnimatingYear(false);
              clearInterval(animationIntervalRef.current);
              animationIntervalRef.current = null;
            }
          }}
          disabled={!selectedMeasure || availableYears.length === 0}
          className="year-slider"
          style={{ 
            width: '180px', 
            margin: '0 8px',
            height: '4px',
            cursor: 'pointer'
          }}
        />
        <div className="year-label" style={{
          minWidth: '40px',
          textAlign: 'center',
          fontSize: '12px',
          fontWeight: '600',
          color: '#333'
        }}>
          {availableYears.length > 0 && availableYears.includes(selectedYear) ? selectedYear : (availableYears[0] || 'N/A')}
        </div>
        <button
          onClick={() => setMapMode(mapMode === '2d' ? '3d' : '2d')}
          style={{
            marginLeft: '12px',
            padding: '4px 8px',
            fontSize: '11px',
            fontWeight: '600',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            minWidth: '32px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          title={mapMode === '2d' ? 'Switch to 3D Globe' : 'Switch to 2D Map'}
        >
          {mapMode === '2d' ? '3D' : '2D'}
        </button>
      </div>
    </div>
  );
};

export default MapView;